import { useState } from 'react';
import { createPortal } from 'react-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Loader2, Mail } from 'lucide-react';
import toast from 'react-hot-toast';
import { Modal } from '@/components/ui';
import { getOrgSubscription } from '@/api/orgs';
import { orgUsersApi } from '@/api/orgUsers';
import { sendTrialWelcome } from '@/api/trialWelcome';
import type { Organization } from '@/types';
import { formatDate } from '@/utils/date';

interface Props { org: Organization }

function InfoRow({ label, value }: { label: string; value?: string | null }) {
  return (
    <div className="flex items-start py-3 border-b border-navy-border last:border-0">
      <span className="w-36 text-xs text-ink-muted flex-shrink-0 pt-0.5">{label}</span>
      <span className="text-sm text-ink-primary num">{value ?? '—'}</span>
    </div>
  );
}

function Badge({ text, className }: { text: string; className: string }) {
  return (
    <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${className}`}>
      {text}
    </span>
  );
}

type WelcomeOwner = {
  owner_name: string;
  owner_email: string;
  owner_phone: string;
};

async function resolveWelcomeOwner(org: Organization): Promise<WelcomeOwner> {
  const users = await orgUsersApi.list(org.id);
  const owner = users.find((u) => u.role === 'owner') ?? users[0];
  const owner_name = owner?.full_name?.trim() || owner?.email || org.name;
  const owner_email = owner?.email?.trim() || org.email?.trim() || '';
  const owner_phone = owner?.phone?.trim() || org.phone?.trim() || '';
  if (!owner_email || !owner_phone) {
    throw new Error('חסר אימייל או טלפון של הבעלים — עדכנו בארגון או במשתמש הבעלים');
  }
  return { owner_name, owner_email, owner_phone };
}

export default function OrgInfoTab({ org }: Props) {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingOwner, setPendingOwner] = useState<WelcomeOwner | null>(null);

  const { data: sub } = useQuery({
    queryKey: ['org-subscription', org.id],
    queryFn: () => getOrgSubscription(org.id),
  });

  const prepareMutation = useMutation({
    mutationFn: () => resolveWelcomeOwner(org),
    onSuccess: (owner) => {
      setPendingOwner(owner);
      setConfirmOpen(true);
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const sendMutation = useMutation({
    mutationFn: async (owner: WelcomeOwner) => {
      const { ok, data } = await sendTrialWelcome({
        org_id: org.id,
        org_name: org.name,
        owner_name: owner.owner_name,
        owner_email: owner.owner_email,
        owner_phone: owner.owner_phone,
        existing_user: true,
      });
      if (!ok || data.success === false) {
        throw new Error(data.error || 'Welcome send failed');
      }
      if (data.skipped) throw new Error('Automatic welcome was already handled; no second message was sent.');
      return data;
    },
    onSuccess: (data) => {
      setConfirmOpen(false);
      setPendingOwner(null);
      const bits = [
        `email_sent=${Boolean(data.email_sent)}`,
        `whatsapp_sent=${Boolean(data.whatsapp_sent)}`,
      ];
      if (Array.isArray(data.errors) && data.errors.length) {
        bits.push(`errors: ${data.errors.join('; ')}`);
      }
      if (data.email_sent && data.whatsapp_sent) {
        toast.success(`Welcome sent — ${bits.join(', ')}`);
      } else {
        toast.error(`Welcome partial/failed — ${bits.join(', ')}`, { duration: 8000 });
      }
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const orgTypeLabel =
    org.org_type === 'individual'
      ? { text: 'פרטי', className: 'bg-slate-100 text-slate-700' }
      : org.org_type === 'osek'
        ? { text: 'עוסק', className: 'bg-blue-100 text-blue-700' }
        : org.org_type === 'company'
          ? { text: 'חברה', className: 'bg-indigo-100 text-indigo-700' }
          : null;

  const onboardingBadge =
    org.onboarding_completed
      ? { text: 'הושלם', className: 'bg-emerald-100 text-emerald-700' }
      : { text: 'ממתין', className: 'bg-amber-100 text-amber-700' };

  const licensingBadge =
    sub?.status === 'trial'
      ? { text: 'ניסיון', className: 'bg-amber-100 text-amber-700' }
      : sub?.status === 'active'
        ? { text: 'פעיל', className: 'bg-emerald-100 text-emerald-700' }
        : sub?.status
          ? { text: sub.status, className: 'bg-slate-100 text-slate-700' }
          : { text: '—', className: 'bg-slate-100 text-slate-600' };

  const fullAddress = [org.address_street, org.address_city].filter(Boolean).join(', ');
  const busy = prepareMutation.isPending || sendMutation.isPending;

  return (
    <div className="space-y-6">
      <div className="card p-6 flex flex-col sm:flex-row sm:items-center gap-4 justify-between">
        <div>
          <h3 className="text-sm font-semibold text-ink-secondary mb-1">הודעת ברוכים הבאים</h3>
          <p className="text-xs text-ink-muted">
            שולח מייל + WhatsApp עם כניסה לחשבון קיים (בלי סיסמה זמנית).
          </p>
        </div>
        <button
          type="button"
          disabled={busy}
          onClick={() => prepareMutation.mutate()}
          className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium text-white transition disabled:opacity-50"
          style={{ background: '#6366f1' }}
        >
          {busy ? <Loader2 size={14} className="animate-spin" /> : <Mail size={14} />}
          שלח הודעת ברוכים הבאים
        </button>
      </div>

      {confirmOpen && pendingOwner && createPortal(
        <Modal
          isOpen
          size="sm"
          title="לאשר שליחת ברוכים הבאים?"
          onClose={() => {
            if (sendMutation.isPending) return;
            setConfirmOpen(false);
            setPendingOwner(null);
          }}
          footer={(
            <div className="flex w-full justify-end gap-2">
              <button
                type="button"
                disabled={sendMutation.isPending}
                onClick={() => {
                  setConfirmOpen(false);
                  setPendingOwner(null);
                }}
                className="rounded-lg border border-navy-border px-4 py-2 text-sm text-ink-secondary hover:bg-navy-hover disabled:opacity-50"
              >
                ביטול
              </button>
              <button
                type="button"
                disabled={sendMutation.isPending}
                onClick={() => sendMutation.mutate(pendingOwner)}
                className="inline-flex items-center gap-2 rounded-lg bg-indigo-500 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-600 disabled:opacity-50"
              >
                {sendMutation.isPending && <Loader2 size={14} className="animate-spin" />}
                שלח
              </button>
            </div>
          )}
        >
          <div dir="rtl" className="space-y-3 text-right text-sm leading-relaxed text-ink-secondary">
            <p>יישלח עם הנחיה להתחבר בסיסמה הקיימת (ללא סיסמה חדשה).</p>
            <p>
              אימייל:{' '}
              <span dir="ltr" className="num inline-block font-medium text-ink-primary">
                {pendingOwner.owner_email}
              </span>
            </p>
            <p>
              טלפון:{' '}
              <span dir="ltr" className="num inline-block font-medium text-ink-primary">
                {pendingOwner.owner_phone}
              </span>
            </p>
          </div>
        </Modal>,
        document.body,
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="card p-6">
          <h3 className="text-sm font-semibold text-ink-secondary mb-2">פרטי ארגון</h3>
          <InfoRow label="שם"     value={org.name} />
          <InfoRow label="אימייל" value={org.email} />
          <InfoRow label="טלפון"  value={org.phone} />
          <InfoRow label="אתר"    value={org.website} />
          <InfoRow label="סטטוס"  value={org.status} />
          <InfoRow label="מטבע"   value={org.currency} />
          <InfoRow label="שפה"    value={org.language} />
        </div>
        <div className="card p-6">
          <h3 className="text-sm font-semibold text-ink-secondary mb-2">מטא</h3>
          <InfoRow label="מזהה"   value={org.id} />
          <InfoRow label="נוצר"   value={formatDate(org.created_at)} />
          <InfoRow label="עודכן"  value={formatDate(org.updated_at)} />
          <InfoRow label="בעלים"  value={org.owner_user_id ?? '—'} />
        </div>
        <div className="card p-6">
          <h3 className="text-sm font-semibold text-ink-secondary mb-2">סוג חשבון ורישוי</h3>
          <div className="flex items-start py-3 border-b border-navy-border">
            <span className="w-36 text-xs text-ink-muted flex-shrink-0 pt-0.5">סוג עסק</span>
            <span className="text-sm text-ink-primary">
              {orgTypeLabel ? <Badge text={orgTypeLabel.text} className={orgTypeLabel.className} /> : '—'}
            </span>
          </div>
          <InfoRow label="מספר זיהוי" value={org.tax_id ?? '—'} />
          <InfoRow label="כתובת" value={fullAddress || '—'} />
          <div className="flex items-start py-3 border-b border-navy-border">
            <span className="w-36 text-xs text-ink-muted flex-shrink-0 pt-0.5">Onboarding</span>
            <span className="text-sm text-ink-primary">
              <Badge text={onboardingBadge.text} className={onboardingBadge.className} />
            </span>
          </div>
          <InfoRow label="תוכנית" value={sub?.subscription_plans?.name ?? '—'} />
          <div className="flex items-start py-3 border-b border-navy-border">
            <span className="w-36 text-xs text-ink-muted flex-shrink-0 pt-0.5">סטטוס רישוי</span>
            <span className="text-sm text-ink-primary">
              <Badge text={licensingBadge.text} className={licensingBadge.className} />
            </span>
          </div>
          <InfoRow label="פג תוקף" value={sub?.expires_at ? formatDate(sub.expires_at) : '—'} />
        </div>
      </div>
    </div>
  );
}
